/**
 * Assessment Report Builder
 *
 * Generates detailed performance reports from assessment attempts.
 * Includes Leerlingprofiel enrichment (Phase 1).
 *
 * DDD co-locatie: moved from components/assessment/utils/ in s59.
 */

import {
  AssessmentReport,
  AssessmentAttempt,
  AssessmentBlueprint,
  ModulePerformance,
  TierPerformance,
  FamilyPerformance,
  PracticeRecommendation,
  ModuleLevelEstimate,
  GrowthIndicator,
  SkillGroupedArea,
  ActionPlan,
} from '../types'
import {
  type ModuleId,
  getFamilyDefinition,
  getFamiliesForModule,
  MODULES,
  getTierRangeForGroup,
  tierToNiveauCode,
  getTierConfig,
} from '../../content'
import type { FamilyMasteryState } from '../../mastery'
import { getFamilyMasteryKey, resolvePhaseByDate } from '../../mastery'
import { GroupId } from '../../config/systemConfig'
import { debug } from '../../utils/logger'
import { tierToCitoLevel } from '../../utils/citoLevelUtils'

// ─── Report Thresholds & Constants ────────────────────────────
// All magic numbers extracted for auditability and single-source-of-truth.

/** Recommendation text thresholds (overall percentage) */
const REC_EXCELLENT_PCT = 85
const REC_GOOD_PCT = 70
const REC_MODERATE_PCT = 55
const REC_TIER_ACCURACY_WEAK = 60

/** Growth indicator thresholds */
const GROWTH_MIN_QUESTIONS = 3
const GROWTH_REGRESSION_URGENT_PRE = 60
const GROWTH_REGRESSION_URGENT_ACC = 30
const GROWTH_REGRESSION_PRE = 50
const GROWTH_REGRESSION_ACC = 40
const GROWTH_FAST_ACC = 80
const GROWTH_FAST_PRE_CEIL = 60
const GROWTH_GOOD_ACC = 70
const GROWTH_GROWING_ACC = 50
const GROWTH_WATCH_ACC = 30

/** Mastery label thresholds (postMastery → label) */
const MASTERY_LABEL_BEGINNER = 30
const MASTERY_LABEL_OEFENAAR = 60
const MASTERY_LABEL_GEVORDERD = 90

/** Report classification thresholds */
const WEAKEST_CONCEPT_ACC = 60
const STRONGEST_CONCEPT_ACC = 80
const WEAK_AREA_MODULE_ACC = 70
const STRONG_AREA_MODULE_ACC = 80
const MAX_WEAK_STRONG_CONCEPTS = 5

/** Suggested practice thresholds */
const PRACTICE_LOW_ACC = 60
const PRACTICE_HIGH_ACC = 80
const PRACTICE_LOW_MASTERY = 70
const PRACTICE_HIGH_POTENTIAL_ACC = 80
const PRACTICE_HIGH_POTENTIAL_MASTERY = 85
const MAX_SUGGESTED_PRACTICE = 5

/** Action plan thresholds */
const ACTION_MEDIUM_ACC_MIN = 50
const ACTION_MEDIUM_ACC_MAX = 80
const ACTION_MEDIUM_MAX_COUNT = 2
const ACTION_STRONG_ACC = 80

/** Tier system */
const MAX_TIER = 16

const DEV = process.env.NODE_ENV === 'development'
function log(...args: unknown[]) {
  if (DEV) debug('[AssessmentReport]', ...args)
}

function generateRecommendation(
  percentage: number,
  tierPerformance: TierPerformance[],
  weakestConcepts: FamilyPerformance[],
  selectedGroup: GroupId,
): string {
  if (percentage >= REC_EXCELLENT_PCT)
    return 'Uitstekend! Je beheerst de stof goed. Focus op uitdagende onderwerpen om je kennis verder te verdiepen.'
  if (percentage >= REC_GOOD_PCT) {
    if (weakestConcepts.length > 0)
      return `Goed gedaan! Je bent op de goede weg. Focus op ${weakestConcepts[0].familyDescription.toLowerCase()} om je resultaat verder te verbeteren.`
    return 'Goed gedaan! Je bent op de goede weg. Blijf oefenen om je kennis te versterken.'
  }
  if (percentage >= REC_MODERATE_PCT) {
    const tier1_6 = tierPerformance.find((t) => t.tierRange === '1-6')
    const tier7_12 = tierPerformance.find((t) => t.tierRange === '7-12')
    if (tier1_6 && tier1_6.accuracy < REC_TIER_ACCURACY_WEAK)
      return 'Focus op de basisvaardigheden (groep 6 niveau). Versterk je fundament voordat je verder gaat.'
    if (tier7_12 && tier7_12.accuracy < REC_TIER_ACCURACY_WEAK)
      return 'Je basiskennis is goed, maar focus op groep 7-8 niveau om verder te komen.'
    if (weakestConcepts.length > 0)
      return `Blijf oefenen! Begin met ${weakestConcepts[0].familyDescription.toLowerCase()} om snel vooruitgang te boeken.`
    return 'Blijf oefenen! Regelmatig oefenen helpt je om de stof beter te begrijpen.'
  }
  if (weakestConcepts.length > 0) {
    const topWeak = weakestConcepts.slice(0, 2)
    const topics = topWeak
      .map((f) => f.familyDescription.toLowerCase())
      .join(' en ')
    return `Geen zorgen, iedereen leert in zijn eigen tempo! Focus eerst op ${topics}. Neem de tijd en vraag hulp als je het nodig hebt.`
  }
  return 'Geen zorgen, iedereen leert in zijn eigen tempo! Blijf oefenen en vraag hulp als je het nodig hebt.'
}

// ─── Leerlingprofiel Enrichment ───────────────────────────────

/**
 * v4.2: Weighted ceiling-based level estimation with SEM.
 *
 * ALGORITHM (CITO-comparable):
 * Instead of simple "average tier of correct answers", uses a weighted
 * ceiling approach that better reflects what CITO measures:
 *
 * 1. Group answers by tier and compute per-tier accuracy
 * 2. Find the "ceiling tier" = highest tier where accuracy >= 50%
 * 3. Compute weighted estimate: correct answers at higher tiers count more
 *    (weight = tier / maxTier), giving credit for harder questions
 * 4. Blend ceiling and weighted average (60/40) for robustness
 * 5. Calculate SEM from tier-level variance for confidence reporting
 *
 * WHY THIS IS DIFFERENT FROM PRACTICE:
 * - Practice uses mastery + LDG + interleaving to OPTIMIZE learning
 * - Assessment uses tier-weighted estimation to MEASURE level
 * - No LDG gating, no mastery influence, no interleaving
 * - Deliberately tests across the FULL tier range (low/mid/high bands)
 *
 * Falls back to simple average when < 3 questions (insufficient data).
 */

/** Minimum questions for weighted estimation (below this: simple average) */
const MIN_QUESTIONS_FOR_WEIGHTED = 3
/** Ceiling accuracy threshold: student "passes" a tier at this accuracy */
const CEILING_ACCURACY_THRESHOLD = 0.5
/** Blend ratio: ceiling vs weighted average (0.6 = 60% ceiling, 40% weighted) */
const CEILING_BLEND_RATIO = 0.6

function computeEstimatedLevels(
  byModule: ModulePerformance[],
  attempt: AssessmentAttempt,
  blueprint: AssessmentBlueprint,
  selectedGroup: GroupId,
): {
  estimatedLevels: ModuleLevelEstimate[]
  overallEstimatedTier: number
  overallEstimatedLevelCode: string
  overallExpectedTier: number
  overallExpectedLevelCode: string
  overallCitoLevel: {
    level: 'I' | 'II' | 'III' | 'IV' | 'V'
    label: string
    color: string
    bgColor: string
    borderColor: string
  }
} {
  const period = resolvePhaseByDate()
  const tierRange = getTierRangeForGroup(selectedGroup)
  let expectedTier = tierRange.min
  if (period === 'M')
    expectedTier += Math.floor((tierRange.max - tierRange.min) / 2)
  if (period === 'E') expectedTier = tierRange.max
  const expectedLevelCode =
    tierToNiveauCode(expectedTier) || `Tier ${expectedTier}`

  const estimatedLevels: ModuleLevelEstimate[] = []
  let totalWeightedTier = 0
  let totalWeight = 0

  byModule.forEach((modPerf) => {
    const module = MODULES.find((m) => m.id === modPerf.moduleId)
    if (!module) return

    const modQuestions = attempt.answers.filter(
      (a) => a.module === modPerf.moduleId,
    )
    if (modQuestions.length === 0) return

    // Resolve tier for each answer from blueprint
    const answersWithTier = modQuestions
      .map((ans) => {
        const bpQ = blueprint.questions.find(
          (q) => q.questionId === ans.questionId,
        )
        return { ...ans, tier: bpQ ? bpQ.tier : 0 }
      })
      .filter((a) => a.tier > 0)

    if (answersWithTier.length === 0) return

    const correctAnswers = answersWithTier.filter((a) => a.isCorrect)
    let estimatedTier: number
    let sem: number | undefined
    let estimationMethod: 'weighted-ceiling' | 'simple-average'

    if (answersWithTier.length >= MIN_QUESTIONS_FOR_WEIGHTED) {
      // ── v4.2: Weighted ceiling estimation ──────────────────────
      estimationMethod = 'weighted-ceiling'

      // Step 1: Group by tier and compute per-tier accuracy
      const tierAccuracy = new Map<number, { correct: number; total: number }>()
      for (const ans of answersWithTier) {
        const entry = tierAccuracy.get(ans.tier) || { correct: 0, total: 0 }
        entry.total++
        if (ans.isCorrect) entry.correct++
        tierAccuracy.set(ans.tier, entry)
      }

      // Step 2: Find ceiling tier (highest tier with accuracy >= threshold)
      const sortedTiers = Array.from(tierAccuracy.entries()).sort(
        ([a], [b]) => a - b,
      )
      let ceilingTier = tierRange.min // default: lowest tier
      for (const [tier, stats] of sortedTiers) {
        if (
          stats.total > 0 &&
          stats.correct / stats.total >= CEILING_ACCURACY_THRESHOLD
        ) {
          ceilingTier = tier
        }
      }

      // Step 3: Weighted average (higher tiers weight more)
      let weightedSum = 0
      let weightTotal = 0
      for (const ans of correctAnswers) {
        const weight = ans.tier / MAX_TIER // Higher tier = more weight
        weightedSum += ans.tier * weight
        weightTotal += weight
      }
      // Include penalty for incorrect low-tier answers
      const incorrectAnswers = answersWithTier.filter((a) => !a.isCorrect)
      for (const ans of incorrectAnswers) {
        if (ans.tier <= ceilingTier) {
          // Failing below ceiling = negative signal (gap in foundation)
          const penalty = 0.3 * (ans.tier / MAX_TIER)
          weightedSum -= ans.tier * penalty
          weightTotal += penalty
        }
        // Failing above ceiling is expected — no penalty
      }
      const weightedAvg =
        weightTotal > 0 ? weightedSum / weightTotal : ceilingTier

      // Step 4: Blend ceiling and weighted average
      estimatedTier =
        CEILING_BLEND_RATIO * ceilingTier +
        (1 - CEILING_BLEND_RATIO) * weightedAvg

      // Step 5: Calculate SEM from tier-level variance
      const tierValues = answersWithTier.map((a) =>
        a.isCorrect ? a.tier : a.tier - 1,
      )
      const mean = tierValues.reduce((s, v) => s + v, 0) / tierValues.length
      const variance =
        tierValues.reduce((s, v) => s + (v - mean) ** 2, 0) / tierValues.length
      const sd = Math.sqrt(variance)
      sem = sd / Math.sqrt(tierValues.length)

      log(
        `Level estimation for ${modPerf.moduleId}: ceiling=${ceilingTier}, ` +
          `weightedAvg=${weightedAvg.toFixed(1)}, blended=${estimatedTier.toFixed(1)}, ` +
          `SEM=±${sem.toFixed(2)} (${answersWithTier.length} questions)`,
      )
    } else {
      // ── Fallback: simple average (too few questions) ───────────
      estimationMethod = 'simple-average'

      if (correctAnswers.length > 0) {
        const sumTiers = correctAnswers.reduce((sum, ans) => sum + ans.tier, 0)
        estimatedTier = sumTiers / correctAnswers.length
      } else {
        const attemptedTiers = answersWithTier.map((a) => a.tier)
        estimatedTier = Math.max(1, Math.min(...attemptedTiers) - 1)
      }
    }

    estimatedTier = Math.max(1, Math.min(MAX_TIER, Math.round(estimatedTier)))
    const estimatedLevelCode =
      tierToNiveauCode(estimatedTier) || `Tier ${estimatedTier}`
    const delta = estimatedTier - expectedTier

    const confidenceBand =
      sem !== undefined
        ? {
            low: Math.max(1, Math.round(estimatedTier - sem)),
            high: Math.min(MAX_TIER, Math.round(estimatedTier + sem)),
          }
        : undefined

    const citoLevel = tierToCitoLevel(estimatedTier, selectedGroup)

    estimatedLevels.push({
      moduleId: modPerf.moduleId,
      moduleTitle: module.title,
      estimatedTier,
      estimatedLevelCode,
      expectedTier,
      expectedLevelCode,
      delta,
      deltaLabel: delta > 0 ? 'boven' : delta === 0 ? 'op' : 'onder',
      questionCount: modQuestions.length,
      accuracy: modPerf.accuracy,
      sem,
      confidenceBand,
      estimationMethod,
      citoLevel,
    })

    // Weight overall by question count (more questions = more reliable)
    totalWeightedTier += estimatedTier * modQuestions.length
    totalWeight += modQuestions.length
  })

  const overallEstimatedTier =
    totalWeight > 0 ? Math.round(totalWeightedTier / totalWeight) : expectedTier
  const overallEstimatedLevelCode =
    tierToNiveauCode(overallEstimatedTier) || `Tier ${overallEstimatedTier}`

  const overallCitoLevel = tierToCitoLevel(overallEstimatedTier, selectedGroup)

  return {
    estimatedLevels,
    overallEstimatedTier,
    overallEstimatedLevelCode,
    overallExpectedTier: expectedTier,
    overallExpectedLevelCode: expectedLevelCode,
    overallCitoLevel,
  }
}

function computeGrowthIndicators(
  byModule: ModulePerformance[],
  byFamily: FamilyPerformance[],
): GrowthIndicator[] {
  return byModule.map((mod) => {
    const modFamilies = byFamily.filter((f) => {
      const def = getFamilyDefinition(f.familyId)
      return def && def.module === mod.moduleId
    })
    const avgPreMastery =
      modFamilies.length > 0
        ? modFamilies.reduce((sum, f) => sum + f.preMastery, 0) /
          modFamilies.length
        : 0
    const accuracy = mod.accuracy

    let level: GrowthIndicator['level'] = 'onbekend'
    let emoji = '⚪'
    let label = 'Onbekend'
    let color = 'text-slate-400'

    if (mod.totalQuestions < GROWTH_MIN_QUESTIONS) {
      level = 'onbekend'
      emoji = '⚪'
      label = 'Te weinig data'
      color = 'text-slate-400'
    } else if (
      avgPreMastery >= GROWTH_REGRESSION_URGENT_PRE &&
      accuracy < GROWTH_REGRESSION_URGENT_ACC
    ) {
      // Had decent mastery but scored very poorly → urgent regression
      level = 'actie_vereist'
      emoji = '🔴⚠'
      label = 'Actie vereist'
      color = 'text-rose-700'
    } else if (
      avgPreMastery >= GROWTH_REGRESSION_PRE &&
      accuracy < GROWTH_REGRESSION_ACC
    ) {
      // Had moderate mastery but scored poorly → declining
      level = 'krimpt'
      emoji = '🔴↓'
      label = 'Krimpt'
      color = 'text-rose-600'
    } else if (
      accuracy >= GROWTH_FAST_ACC &&
      avgPreMastery < GROWTH_FAST_PRE_CEIL
    ) {
      level = 'groei_snel'
      emoji = '🟢↗'
      label = 'Ik groei snel'
      color = 'text-emerald-600'
    } else if (accuracy >= GROWTH_GOOD_ACC) {
      level = 'gaat_goed'
      emoji = '🟢→'
      label = 'Het gaat goed'
      color = 'text-emerald-600'
    } else if (accuracy >= GROWTH_GROWING_ACC) {
      level = 'ik_groei'
      emoji = '🟡→'
      label = 'Ik groei'
      color = 'text-yellow-600'
    } else if (accuracy >= GROWTH_WATCH_ACC) {
      level = 'let_op'
      emoji = '🟠↘'
      label = 'Let op mij'
      color = 'text-orange-600'
    } else {
      level = 'extra_aandacht'
      emoji = '🔴↓'
      label = 'Extra aandacht'
      color = 'text-rose-600'
    }

    return { moduleId: mod.moduleId, level, emoji, label, color }
  })
}

function groupFamiliesBySkill(
  families: FamilyPerformance[],
): SkillGroupedArea[] {
  const grouped = new Map<ModuleId, Map<string, FamilyPerformance[]>>()

  families.forEach((f) => {
    const def = getFamilyDefinition(f.familyId)
    if (!def) return
    if (!grouped.has(def.module)) grouped.set(def.module, new Map())
    const moduleGroup = grouped.get(def.module)!
    if (!moduleGroup.has(def.skill)) moduleGroup.set(def.skill, [])
    moduleGroup.get(def.skill)!.push(f)
  })

  const result: SkillGroupedArea[] = []
  grouped.forEach((skillsMap, moduleId) => {
    const moduleDef = MODULES.find((m) => m.id === moduleId)
    const skills: SkillGroupedArea['skills'] = []

    skillsMap.forEach((fams, skillName) => {
      const avgAcc = fams.reduce((sum, f) => sum + f.accuracy, 0) / fams.length
      skills.push({
        skillName: skillName.replace(/_/g, ' '),
        families: fams.map((f) => ({
          familyId: f.familyId,
          description: f.familyDescription,
          accuracy: f.accuracy,
          masteryLevel:
            f.postMastery < MASTERY_LABEL_BEGINNER
              ? 'beginner'
              : f.postMastery < MASTERY_LABEL_OEFENAAR
                ? 'oefenaar'
                : f.postMastery < MASTERY_LABEL_GEVORDERD
                  ? 'gevorderd'
                  : 'meester',
        })),
        avgAccuracy: avgAcc,
      })
    })

    if (skills.length > 0) {
      result.push({
        moduleId,
        moduleTitle: moduleDef ? moduleDef.title : moduleId,
        skills: skills.sort((a, b) => a.avgAccuracy - b.avgAccuracy),
      })
    }
  })
  return result
}

function buildActionPlan(
  weakestConcepts: FamilyPerformance[],
  byFamily: FamilyPerformance[],
  estimatedLevels: ModuleLevelEstimate[],
): ActionPlan {
  let beginMet = null
  if (weakestConcepts.length > 0) {
    const weakest = weakestConcepts[0]
    const def = getFamilyDefinition(weakest.familyId)
    const mod = MODULES.find((m) => m.id === def?.module)
    if (def && mod) {
      beginMet = {
        moduleTitle: mod.title,
        skillName: def.skill.replace(/_/g, ' '),
        familyDescription: weakest.familyDescription,
        moduleId: def.module,
      }
    }
  }

  const gaVerderMet: ActionPlan['gaVerderMet'] = []
  const mediumFamilies = byFamily
    .filter(
      (f) =>
        f.accuracy >= ACTION_MEDIUM_ACC_MIN &&
        f.accuracy < ACTION_MEDIUM_ACC_MAX,
    )
    .slice(0, ACTION_MEDIUM_MAX_COUNT)
  for (const f of mediumFamilies) {
    const def = getFamilyDefinition(f.familyId)
    const mod = MODULES.find((m) => m.id === def?.module)
    if (def && mod) {
      gaVerderMet.push({
        moduleTitle: mod.title,
        skillName: def.skill.replace(/_/g, ' '),
        familyDescription: f.familyDescription,
        moduleId: def.module,
      })
    }
  }

  let uitdaging = null
  const strongModule = estimatedLevels.find(
    (m) => m.accuracy >= ACTION_STRONG_ACC,
  )
  if (strongModule) {
    uitdaging = {
      moduleTitle: strongModule.moduleTitle,
      suggestion: `Probeer een niveau hoger in ${strongModule.moduleTitle}!`,
      moduleId: strongModule.moduleId,
    }
  }

  return { beginMet, gaVerderMet, uitdaging }
}

// ─── Main Export ──────────────────────────────────────────────

export function buildAssessmentReport(
  attempt: AssessmentAttempt,
  blueprint: AssessmentBlueprint,
  preFamilyMastery: Record<string, FamilyMasteryState>,
  postFamilyMastery: Record<string, FamilyMasteryState>,
): AssessmentReport {
  log(`Building report for attempt ${attempt.id}`)
  const { answers, selectedGroup } = attempt
  const correctCount = answers.filter((a) => a.isCorrect).length
  const totalQuestions = answers.length
  const percentage =
    totalQuestions > 0 ? Math.round((correctCount / totalQuestions) * 100) : 0
  const totalSeconds = attempt.totalTimeSeconds
  const avgPerQuestion =
    totalQuestions > 0 ? Math.round(totalSeconds / totalQuestions) : 0

  // Module breakdown
  const moduleMap = new Map<
    ModuleId,
    { correct: number; total: number; time: number }
  >()
  answers.forEach((answer) => {
    const current = moduleMap.get(answer.module) || {
      correct: 0,
      total: 0,
      time: 0,
    }
    current.total++
    if (answer.isCorrect) current.correct++
    current.time += answer.timeSpent
    moduleMap.set(answer.module, current)
  })
  const byModule: ModulePerformance[] = Array.from(moduleMap.entries()).map(
    ([moduleId, stats]) => ({
      moduleId,
      totalQuestions: stats.total,
      correctCount: stats.correct,
      accuracy: Math.round((stats.correct / stats.total) * 100),
      avgTimePerQuestion: Math.round(stats.time / stats.total),
    }),
  )

  // Tier breakdown
  const tierMap = new Map<string, { correct: number; total: number }>()
  answers.forEach((answer) => {
    const blueprintQ = blueprint.questions.find(
      (q) => q.questionId === answer.questionId,
    )
    if (!blueprintQ) return
    const tierRange =
      blueprintQ.tier <= 6 ? '1-6' : blueprintQ.tier <= 12 ? '7-12' : '13-16'
    const current = tierMap.get(tierRange) || { correct: 0, total: 0 }
    current.total++
    if (answer.isCorrect) current.correct++
    tierMap.set(tierRange, current)
  })
  const byTier: TierPerformance[] = Array.from(tierMap.entries())
    .map(([tierRange, stats]) => ({
      tierRange,
      totalQuestions: stats.total,
      correctCount: stats.correct,
      accuracy: Math.round((stats.correct / stats.total) * 100),
    }))
    .sort((a, b) => {
      const order = { '1-6': 1, '7-12': 2, '13-16': 3 }
      return (
        (order[a.tierRange as keyof typeof order] || 0) -
        (order[b.tierRange as keyof typeof order] || 0)
      )
    })

  // Family breakdown
  const familyMap = new Map<
    string,
    { correct: number; total: number; time: number }
  >()
  answers.forEach((answer) => {
    const current = familyMap.get(answer.familyId) || {
      correct: 0,
      total: 0,
      time: 0,
    }
    current.total++
    if (answer.isCorrect) current.correct++
    current.time += answer.timeSpent
    familyMap.set(answer.familyId, current)
  })
  const byFamily: FamilyPerformance[] = Array.from(familyMap.entries())
    .map(([familyId, stats]) => {
      const def = getFamilyDefinition(familyId)
      if (!def) return null
      const preKey = getFamilyMasteryKey(familyId, selectedGroup)
      const preMastery = preFamilyMastery[preKey]?.masteryScore || 0
      const postKey = getFamilyMasteryKey(familyId, selectedGroup)
      const postMastery = postFamilyMastery[postKey]?.masteryScore || 0
      return {
        familyId,
        familyDescription: def.description,
        totalQuestions: stats.total,
        correctCount: stats.correct,
        accuracy: Math.round((stats.correct / stats.total) * 100),
        preMastery,
        postMastery,
        delta: postMastery - preMastery,
      }
    })
    .filter((f): f is FamilyPerformance => f !== null)
    .sort((a, b) => a.accuracy - b.accuracy)

  const weakestConcepts = byFamily
    .filter((f) => f.accuracy < WEAKEST_CONCEPT_ACC)
    .slice(0, MAX_WEAK_STRONG_CONCEPTS)
  const strongestConcepts = byFamily
    .filter((f) => f.accuracy > STRONGEST_CONCEPT_ACC)
    .sort((a, b) => b.accuracy - a.accuracy)
    .slice(0, MAX_WEAK_STRONG_CONCEPTS)
  const weakAreas = byModule
    .filter((m) => m.accuracy < WEAK_AREA_MODULE_ACC)
    .sort((a, b) => a.accuracy - b.accuracy)
    .map((m) => {
      const module = MODULES.find((mod) => mod.id === m.moduleId)
      return module?.title || m.moduleId
    })
  const strongAreas = byModule
    .filter((m) => m.accuracy > STRONG_AREA_MODULE_ACC)
    .sort((a, b) => b.accuracy - a.accuracy)
    .map((m) => {
      const module = MODULES.find((mod) => mod.id === m.moduleId)
      return module?.title || m.moduleId
    })

  // Suggested practice
  const suggestedPractice: PracticeRecommendation[] = []
  weakestConcepts.forEach((family) => {
    suggestedPractice.push({
      familyId: family.familyId,
      familyDescription: family.familyDescription,
      currentMastery: family.postMastery,
      reason: 'weak_performance',
      priority: 1,
    })
  })
  byFamily
    .filter(
      (f) =>
        f.accuracy >= PRACTICE_LOW_ACC &&
        f.accuracy <= PRACTICE_HIGH_ACC &&
        f.postMastery < PRACTICE_LOW_MASTERY &&
        !suggestedPractice.some((s) => s.familyId === f.familyId),
    )
    .forEach((family) => {
      suggestedPractice.push({
        familyId: family.familyId,
        familyDescription: family.familyDescription,
        currentMastery: family.postMastery,
        reason: 'low_mastery',
        priority: 2,
      })
    })
  byFamily
    .filter(
      (f) =>
        f.accuracy > PRACTICE_HIGH_POTENTIAL_ACC &&
        f.postMastery < PRACTICE_HIGH_POTENTIAL_MASTERY &&
        !suggestedPractice.some((s) => s.familyId === f.familyId),
    )
    .forEach((family) => {
      suggestedPractice.push({
        familyId: family.familyId,
        familyDescription: family.familyDescription,
        currentMastery: family.postMastery,
        reason: 'high_potential',
        priority: 3,
      })
    })
  suggestedPractice.sort((a, b) => a.priority - b.priority)

  const recommendation = generateRecommendation(
    percentage,
    byTier,
    weakestConcepts,
    selectedGroup,
  )

  // Leerlingprofiel enrichment
  const {
    estimatedLevels,
    overallEstimatedTier,
    overallEstimatedLevelCode,
    overallExpectedTier,
    overallExpectedLevelCode,
    overallCitoLevel,
  } = computeEstimatedLevels(byModule, attempt, blueprint, selectedGroup)
  const growthIndicators = computeGrowthIndicators(byModule, byFamily)
  const strengthsBySkill = groupFamiliesBySkill(strongestConcepts)
  const focusAreasBySkill = groupFamiliesBySkill(weakestConcepts)
  const actionPlan = buildActionPlan(weakestConcepts, byFamily, estimatedLevels)

  return {
    attemptId: attempt.id,
    blueprintId: attempt.blueprintId,
    completedAt: attempt.completedAt ?? Date.now(),
    selectedGroup,
    assignmentId: attempt.assignmentId,
    score: { correctCount, totalQuestions, percentage },
    time: { totalSeconds, avgPerQuestion },
    byModule,
    byTier,
    byFamily,
    weakestConcepts,
    strongestConcepts,
    weakAreas,
    strongAreas,
    suggestedPractice: suggestedPractice.slice(0, MAX_SUGGESTED_PRACTICE),
    recommendation,
    answers: attempt.answers,
    estimatedLevels,
    overallEstimatedTier,
    overallEstimatedLevelCode,
    overallExpectedTier,
    overallExpectedLevelCode,
    overallCitoLevel,
    growthIndicators,
    strengthsBySkill,
    focusAreasBySkill,
    actionPlan,
  }
}
